import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Icon } from "@iconify/react";
import { useState } from "react";

gsap.registerPlugin(ScrollTrigger);

const ContactUs = () => {
    const sectionRef = useRef(null);
    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const [showPopup, setShowPopup] = useState(false);


    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");

    const contactUsContent = [
        {
            id: 1,
            icon: "mdi:contact",
            title: "Call Us",
            info: "+971 528805272",
        },
        {
            id: 2,
            icon: "mdi:email",
            title: "Email Us",
            info: "info@remarkhr.com",
        },
        {
            id: 3,
            icon: "mdi:open-in-new",
            title: "Open Hours",
            info: "Mon - Fri 9:00AM - 06:00PM",
        },
    ];


    // 📨 Handle Input Change
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // 📨 Handle Submit (POST API)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResponseMessage("");
        setShowPopup(false);


        const payload = {
            li_subject: formData.subject,
            li_name: formData.name,
            li_email: formData.email,
            li_message: formData.message,
            li_company: "Visko HR Private Limted",
        };

        try {
            const res = await fetch(
                "https://api.visko.group/api/inquiry/message/insert-message",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (res.ok) {
                setResponseMessage("✅ Message sent successfully!");
                setFormData({ name: "", email: "", subject: "", message: "" });
            } else {
                setResponseMessage("❌ Failed to send message. Try again.");
            }
        } catch (error) {
            console.error(error);
            setResponseMessage("⚠️ Network error. Please try later.");
        } finally {
            setLoading(false);
            setShowPopup(true);
            setTimeout(() => setShowPopup(false), 3000);
        }
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from(leftRef.current, {
                x: -100,
                opacity: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 80%",
                    toggleActions: "play none none reverse",
                },
            });

            gsap.from(rightRef.current, {
                x: 100,
                opacity: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 80%",
                    toggleActions: "play none none reverse",
                },
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section id="contact"
            ref={sectionRef}
            className="w-full bg-black text-white lg:py-20 py-6 px-6 sm:px-10 lg:px-20 overflow-hidden"
        >
            <div className="w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                {/* POPUP MESSAGE */}
                {showPopup && (
                    <div
                        className={`fixed bottom-8 right-6 z-[9999] px-6 py-3 rounded-lg shadow-lg text-sm font-medium transition-all duration-500 ${responseMessage.includes("✅")
                            ? "bg-green-600 text-white"
                            : responseMessage.includes("⚠️")
                                ? "bg-yellow-600 text-white"
                                : "bg-red-600 text-white"
                            } animate-slide-in`}
                    >
                        {responseMessage}
                    </div>
                )}
                {/* LEFT SECTION */}
                <div ref={leftRef} className="space-y-8">

                    <button className="bg-white/10 border border-white/20 px-4 sm:px-6 py-2  rounded-full text-xs sm:text-sm hover:bg-white/20 transition-all flex items-center">
                        <Icon
                            icon="material-symbols:lightbulb-2-rounded"
                            className="mr-2 text-[#F26A33] drop-shadow-[0_0_8px_#F26A33]"
                            width="22"
                            height="22"
                        />
                        CONTACT US
                    </button>

                    {/* Heading */}
                    <h2 className="animate-text text-4xl lg:text-4xl xl:text-7xl font-light text-gray-400 mb-4 leading-tight">
                        Get in Touch {" "}
                        <span className="text-white font-light">with Our Team!</span>
                    </h2>

                    {/* Service Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                        {contactUsContent.map((service) => (
                            <div
                                key={service.id}
                                className="bg-neutral-900 md:p-6 p-3 rounded-2xl border border-neutral-800 hover:border-orange-500 hover:scale-105 transition-all duration-300 group"
                            >
                                <div className="text-orange-500 mb-3">
                                    <Icon icon={service.icon} width="36" height="36" />
                                </div>
                                <h3 className="md:text-lg font-medium leading-snug group-hover:text-white transition-colors">
                                    {service.title}
                                </h3>
                                <p className="text-[#99A1AF] text-xs sm:text-base font-medium leading-relaxed pt-2 break-words">
                                    {service.info}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT FORM SECTION */}
                <div ref={rightRef}>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your full name"
                                required
                                className="bg-[#1c1c1c] w-full px-5 py-4 rounded-2xl outline-none focus:ring-2 focus:ring-[#F26A33] transition-all text-sm sm:text-base"
                            />
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="E-mail address"
                                required
                                className="bg-[#1c1c1c] w-full px-5 py-4 rounded-2xl outline-none focus:ring-2 focus:ring-[#F26A33] transition-all text-sm sm:text-base"
                            />
                        </div>

                        <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Subject"
                            required
                            className="bg-[#1c1c1c] w-full px-5 py-4 rounded-2xl outline-none focus:ring-2 focus:ring-[#F26A33] transition-all text-sm sm:text-base"
                        />

                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Message"
                            rows="5"
                            required
                            className="bg-[#1c1c1c] w-full px-5 py-4 rounded-2xl outline-none resize-none focus:ring-2 focus:ring-[#F26A33] transition-all text-sm sm:text-base"
                        />

                        {/* Send Button */}
                        <button type="submit"
                            disabled={loading} className={`${loading ? "bg-gray-600" : "bg-[#F26A33] hover:bg-orange-600"
                                } relative inline-flex items-center justify-center px-10 py-3 overflow-hidden tracking-tighter text-white bg-orange-600 rounded-md group`} >
                            <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-gray-600 rounded-full group-hover:w-56 group-hover:h-56"></span>
                            <span className="absolute bottom-0 left-0 h-full -ml-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-auto h-full opacity-100 object-stretch" viewBox="0 0 487 487" >
                                    <path
                                        fill-opacity=".1"
                                        fill-rule="nonzero"
                                        fill="#FFF"
                                        d="M0 .3c67 2.1 134.1 4.3 186.3 37 52.2 32.7 89.6 95.8 112.8 150.6 23.2 54.8 32.3 101.4 61.2 149.9 28.9 48.4 77.7 98.8 126.4 149.2H0V.3z"
                                    ></path>
                                </svg>
                            </span>
                            <span className="absolute top-0 right-0 w-12 h-full -mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="object-cover w-full h-full" viewBox="0 0 487 487" >
                                    <path
                                        fill-opacity=".1"
                                        fill-rule="nonzero"
                                        fill="#FFF"
                                        d="M487 486.7c-66.1-3.6-132.3-7.3-186.3-37s-95.9-85.3-126.2-137.2c-30.4-51.8-49.3-99.9-76.5-151.4C70.9 109.6 35.6 54.8.3 0H487v486.7z"
                                    ></path>
                                </svg>
                            </span>
                            <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-200"></span>
                            <span className="relative text-base font-semibold">Submit</span>
                        </button>

                        {/* {responseMessage && (
                            <p className="pt-2 text-sm text-center text-gray-300">
                                {responseMessage}
                            </p>
                        )} */}
                    </form>
                </div>

            </div>
        </section>
    );
};

export default ContactUs;
